import {User} from '../../features/auth/models/User';

/**
 * Security Audit Logger - Lightweight security-focused audit logging
 * Only logs critical security events, not all app activity
 * Integrates seamlessly with existing components
 */

export enum SecurityEventType {
  UNAUTHORIZED_ACCESS = 'unauthorized_access',
  LOGIN_FAILURE = 'login_failure',
  PERMISSION_DENIED = 'permission_denied',
  SENSITIVE_DATA_ACCESS = 'sensitive_data_access',
  ROLE_ELEVATION = 'role_elevation',
  EXPORT_ATTEMPT = 'export_attempt',
  IMPORT_ATTEMPT = 'import_attempt'
}

export interface SecurityEvent {
  id: string;
  timestamp: Date;
  userId?: number;
  userRole?: string;
  eventType: SecurityEventType;
  description: string;
  resource?: string;
  action?: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  ipAddress?: string;
  userAgent?: string;
  additionalData?: Record<string, any>;
}

class SecurityAuditLogger {
  private static instance: SecurityAuditLogger;
  private events: SecurityEvent[] = [];
  private maxEvents = 500; // Keep only recent events
  private isEnabled = true;

  private constructor () {}

  public static getInstance (): SecurityAuditLogger {
    if (!SecurityAuditLogger.instance) {
      SecurityAuditLogger.instance = new SecurityAuditLogger();
    }
    return SecurityAuditLogger.instance;
  }

  /**
   * Enable/disable security logging
   */
  public setEnabled (enabled: boolean): void {
    this.isEnabled = enabled;
  }

  /**
   * Log unauthorized access attempt
   */
  public logUnauthorizedAccess (
    user: User | null,
    resource: string,
    action: string,
    permission?: string
  ): void {
    if (!this.isEnabled) {return;}

    this.addEvent({
      eventType: SecurityEventType.UNAUTHORIZED_ACCESS,
      description: `Unauthorized access attempt: ${action} on ${resource}`,
      resource,
      action,
      severity: 'high',
      additionalData: {permission, userId: user?.id, userRole: user?.role.name}
    });
  }

  /**
   * Log permission denied
   */
  public logPermissionDenied (
    user: User | null,
    permission: string,
    resource?: string
  ): void {
    if (!this.isEnabled) {return;}

    this.addEvent({
      eventType: SecurityEventType.PERMISSION_DENIED,
      description: `Permission denied: ${permission}`,
      resource,
      severity: 'medium',
      additionalData: {permission, userId: user?.id, userRole: user?.role.name}
    });
  }

  /**
   * Log sensitive data access
   */
  public logSensitiveDataAccess (
    user: User | null,
    resource: string,
    recordCount: number
  ): void {
    if (!this.isEnabled) {return;}

    this.addEvent({
      eventType: SecurityEventType.SENSITIVE_DATA_ACCESS,
      description: `Sensitive data access: ${resource} (${recordCount} records)`,
      resource,
      severity: 'medium',
      additionalData: {recordCount, userId: user?.id, userRole: user?.role.name}
    });
  }

  /**
   * Log login failure
   */
  public logLoginFailure (
    username: string,
    reason: string,
    ipAddress?: string
  ): void {
    if (!this.isEnabled) {return;}

    this.addEvent({
      eventType: SecurityEventType.LOGIN_FAILURE,
      description: `Login failure: ${username} - ${reason}`,
      severity: 'medium',
      ipAddress,
      additionalData: {username, reason}
    });
  }

  /**
   * Log export attempt
   */
  public logExportAttempt (
    user: User | null,
    resource: string,
    recordCount: number,
    format: string
  ): void {
    if (!this.isEnabled) {return;}

    this.addEvent({
      eventType: SecurityEventType.EXPORT_ATTEMPT,
      description: `Data export: ${resource} (${recordCount} records, ${format})`,
      resource,
      severity: 'medium',
      additionalData: {recordCount, format, userId: user?.id, userRole: user?.role.name}
    });
  }

  /**
   * Log import attempt
   */
  public logImportAttempt (
    user: User | null,
    resource: string,
    recordCount: number,
    format: string
  ): void {
    if (!this.isEnabled) {return;}

    this.addEvent({
      eventType: SecurityEventType.IMPORT_ATTEMPT,
      description: `Data import: ${resource} (${recordCount} records, ${format})`,
      resource,
      severity: 'high',
      additionalData: {recordCount, format, userId: user?.id, userRole: user?.role.name}
    });
  }

  /**
   * Add event to log
   */
  private addEvent (eventData: Omit<SecurityEvent, 'id' | 'timestamp'>): void {
    const event: SecurityEvent = {
      id: `security_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
      ...eventData
    };

    this.events.push(event);

    // Keep only recent events
    if (this.events.length > this.maxEvents) {
      this.events = this.events.slice(-this.maxEvents);
    }

    // Security events are logged through the audit system
    // No console logging in production
  }

  /**
   * Get recent security events
   */
  public getRecentEvents (limit: number = 50): SecurityEvent[] {
    return this.events
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
      .slice(0, limit);
  }

  /**
   * Get events by type
   */
  public getEventsByType (eventType: SecurityEventType): SecurityEvent[] {
    return this.events.filter(event => event.eventType === eventType);
  }

  /**
   * Get security statistics
   */
  public getSecurityStats (): {
    totalEvents: number;
    unauthorizedAccessCount: number;
    permissionDeniedCount: number;
    lastEventDate?: Date;
  } {
    const unauthorizedAccessCount = this.events.filter(
      e => e.eventType === SecurityEventType.UNAUTHORIZED_ACCESS
    ).length;

    const permissionDeniedCount = this.events.filter(
      e => e.eventType === SecurityEventType.PERMISSION_DENIED
    ).length;

    return {
      totalEvents: this.events.length,
      unauthorizedAccessCount,
      permissionDeniedCount,
      lastEventDate: this.events.length > 0 ? this.events[this.events.length - 1].timestamp : undefined
    };
  }

  /**
   * Clear old events (keep only last 24 hours)
   */
  public clearOldEvents (): void {
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
    this.events = this.events.filter(event => event.timestamp > oneDayAgo);
  }
}

export default SecurityAuditLogger;
